import React, { useCallback, useMemo, useState } from "react";
import PropTypes from "prop-types";
import i18n from "i18next";
import IdealStudio from "../../../../core/editor/main";
import { isSlide, isContainedView } from "../../../../common/utils";
import { ID_PREFIX_BOX } from "../../../../common/constants";
import { randomPositionGenerator } from "../../clipboard/clipboard.utils";
import { createBox } from "../../../../common/common_tools";
import getMediaBoxSize from "../../../utils/getMediaBoxSize";

function FlickrPanel({
  onElementSelected,
  onBoxAdded,
  boxes,
  navItems,
  navItemSelected,
  containedViews,
  containedViewSelected,
  boxSelected,
  pluginToolbars,
}) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [msg, setMsg] = useState(i18n.t("FileModal.APIProviders.no_files"));
  const [loading, setLoading] = useState(false);

  const hasResults = useMemo(() => results && results.length > 0, [results]);

  const handleSearch = useCallback(
    async (text) => {
      const q = (text || query || "").trim();
      if (!q) return;

      try {
        setLoading(true);
        setMsg(i18n.t("FileModal.APIProviders.searching"));
        setResults([]);

        // Modern equivalent of FlickrComponent's $.getJSON JSONP call, but using nojsoncallback=1
        const flickrURL =
          "https://api.flickr.com/services/feeds/photos_public.gne?tags=" +
          encodeURIComponent(q) +
          "&tagmode=all&format=json&nojsoncallback=1";

        const res = await fetch(flickrURL);
        const imgs = await res.json();

        if (imgs && Array.isArray(imgs.items)) {
          const mapped = imgs.items.map((img) => ({
            title: img.title,
            // Match modal logic: use bigger image by stripping _m
            url: img.media && img.media.m ? img.media.m.replace(/_m/i, "") : "",
          }));
          setResults(mapped);
          setMsg(mapped.length > 0 ? "" : i18n.t("FileModal.APIProviders.no_files"));
        } else {
          setResults([]);
          setMsg(i18n.t("FileModal.APIProviders.no_files"));
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
        setResults([]);
        setMsg(i18n.t("FileModal.APIProviders.error"));
      } finally {
        setLoading(false);
      }
    },
    [query]
  );

  const addImageToScene = async (imageUrl) => {
    if (!onBoxAdded || !imageUrl || !boxes || !navItems || !containedViews) return;

    const pluginName = "HotspotImages";
    const plugin = IdealStudio.Plugins.get(pluginName);
    if (!plugin) return;

    const navItem =
      containedViewSelected && containedViews[containedViewSelected]
        ? containedViews[containedViewSelected]
        : navItems[navItemSelected];

    if (!navItem) return;

    const cv =
      containedViewSelected &&
      containedViews[containedViewSelected] &&
      isContainedView(containedViewSelected);
    const cvslide = cv && isSlide(navItem.type);
    const inASlide = (!cv && isSlide(navItem.type)) || cvslide;
    const parent = inASlide ? navItem.id : navItem.boxes && navItem.boxes[0];

    const { width, height, widthUnit, heightUnit } = await getMediaBoxSize(imageUrl, "image");

    const initialParams = {
      parent,
      container: 0,
      name: pluginName,
      position: {
        x: randomPositionGenerator(10, 30),
        y: randomPositionGenerator(10, 30),
        type: "absolute",
      },
      id: ID_PREFIX_BOX + Date.now(),
      page: cv ? containedViewSelected : navItem.id || navItemSelected || 0,
      url: imageUrl,
      width,
      height,
      widthUnit,
      heightUnit,
    };

    createBox(initialParams, pluginName, inASlide, onBoxAdded, boxes);
  };

  const handleResultDoubleClick = (item) => {
    if (!item.url) return;

    addImageToScene(item.url);

    if (onElementSelected) {
      onElementSelected(item.title, item.url, "image", undefined);
    }
  };

  return (
    <div
      className="library-flickr-panel"
      style={{
        flex: 1,
        padding: 12,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 8,
          marginBottom: 12,
        }}
      >
        <div
          style={{
            fontSize: 14,
            fontWeight: 600,
          }}
        >
          Flickr
        </div>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSearch();
          }}
          style={{ display: "flex", flexDirection: "column", gap: 8 }}
        >
          <input
            type="text"
            placeholder={i18n.t("Search") || "Ara"}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            style={{
              width: "100%",
              height: 36,
              padding: "0 10px",
              borderRadius: 999,
              border: "1px solid #e5e7eb",
              outline: "none",
              fontSize: 13,
              backgroundColor: "#f9fafb",
            }}
          />
          <button
            type="submit"
            disabled={loading || !query.trim()}
            style={{
              height: 34,
              borderRadius: 999,
              border: "none",
              backgroundColor: loading ? "#cbd5f5" : "#6366f1",
              color: "white",
              fontSize: 13,
              fontWeight: 500,
              cursor: loading || !query.trim() ? "default" : "pointer",
            }}
          >
            {loading
              ? i18n.t("FileModal.APIProviders.searching") || "Aranıyor..."
              : i18n.t("vish_search_button") || "Ara"}
          </button>
        </form>
      </div>

      <div
        style={{
          flex: 1,
          overflowY: "auto",
          paddingRight: 4,
        }}
      >
        {!hasResults && msg && (
          <div
            style={{
              fontSize: 12,
              color: "#6b7280",
            }}
          >
            {msg}
          </div>
        )}

        {hasResults && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(88px, 1fr))",
              gap: 8,
            }}
          >
            {results.map((item, index) => (
              <button
                key={index}
                type="button"
                onDoubleClick={() => handleResultDoubleClick(item)}
                style={{
                  border: "none",
                  padding: 0,
                  background: "transparent",
                  cursor: "pointer",
                }}
              >
                <div
                  style={{
                    width: "100%",
                    aspectRatio: "1 / 1",
                    borderRadius: 10,
                    overflow: "hidden",
                    backgroundColor: "#e5e7eb",
                  }}
                >
                  {item.url ? (
                    <img
                      src={item.url}
                      alt={item.title}
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                  ) : null}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

FlickrPanel.propTypes = {
  onElementSelected: PropTypes.func,
  onBoxAdded: PropTypes.func,
  boxes: PropTypes.object,
  navItems: PropTypes.object,
  navItemSelected: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  containedViews: PropTypes.object,
  containedViewSelected: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  boxSelected: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  pluginToolbars: PropTypes.object,
};

export default FlickrPanel;
